import React from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  Chip, 
  Tabs, 
  Tab,
  Avatar 
} from '@material-ui/core';
import { 
  CloudDownload, 
  CheckCircle, 
  Timeline, 
  TrendingUp,
  Build,
  Schedule 
} from '@material-ui/icons';
import { Link } from "@backstage/core-components";
import { 
  FilteredProjectDeploymentData, 
  ProcessedDeployment,
  FilteredWeeklyDeployments 
} from '../types';
import { 
  getDeploymentStatusIcon, 
  getDeploymentStatusChip, 
  getDeploymentStatusColor,
  formatDate, 
  formatDateShort 
} from '../utils';
import { TabPanel } from '../shared';

// Deployment overview cards component
interface DeploymentOverviewCardsProps {
  deploymentData: FilteredProjectDeploymentData;
}

export const DeploymentOverviewCards: React.FC<DeploymentOverviewCardsProps> = ({ deploymentData }) => (
  <Grid container spacing={3} style={{ marginBottom: '24px' }}>
    <Grid item xs={12} sm={6} md={3}>
      <Card elevation={2}>
        <CardContent style={{ textAlign: 'center', padding: '20px' }}>
          <CloudDownload style={{ fontSize: 40, color: '#2196f3', marginBottom: '8px' }} />
          <Typography variant="h4" style={{ fontWeight: 'bold', color: '#2196f3' }}>
            {deploymentData.summary?.totalUpcomingDeployments}
          </Typography>
          <Typography variant="body2" color="textSecondary">Upcoming Beta/Prod</Typography>
          <Typography variant="caption" style={{ color: '#4caf50' }}>
            This week
          </Typography>
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <Card elevation={2}>
        <CardContent style={{ textAlign: 'center', padding: '20px' }}>
          <CheckCircle style={{ fontSize: 40, color: '#4caf50', marginBottom: '8px' }} />
          <Typography variant="h4" style={{ fontWeight: 'bold', color: '#4caf50' }}>
            {deploymentData.summary?.successRateLast4Weeks?.toFixed(1) ?? 'N/A'}%
          </Typography>
          <Typography variant="body2" color="textSecondary">Prod Success Rate</Typography>
          <Typography variant="caption" color="textSecondary">
            Last 4 weeks
          </Typography>
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <Card elevation={2}>
        <CardContent style={{ textAlign: 'center', padding: '20px' }}>
          <TrendingUp style={{ fontSize: 40, color: '#ff9800', marginBottom: '8px' }} />
          <Typography variant="h4" style={{ fontWeight: 'bold', color: '#ff9800' }}>
            {deploymentData.summary?.averageDeploymentsPerWeek?.toFixed(1) ?? 'N/A'}
          </Typography>
          <Typography variant="body2" color="textSecondary">Avg Prod/Week</Typography>
          <Typography variant="caption" color="textSecondary">
            Deployment frequency
          </Typography>
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <Card elevation={2}>
        <CardContent style={{ textAlign: 'center', padding: '20px' }}>
          <Timeline style={{ fontSize: 40, color: '#9c27b0', marginBottom: '8px' }} />
          <Typography variant="h4" style={{ fontWeight: 'bold', color: '#9c27b0' }}>
            {deploymentData.summary?.totalCompletedLast4Weeks}
          </Typography>
          <Typography variant="body2" color="textSecondary">Prod Completed</Typography>
          <Typography variant="caption" color="textSecondary">
            Last 4 weeks
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  </Grid>
);

// Deployment list component
interface DeploymentListProps {
  deployments: ProcessedDeployment[];
  onDeploymentSelect: (deployment: ProcessedDeployment) => void;
  title?: string;
}

export const DeploymentList: React.FC<DeploymentListProps> = ({ deployments, onDeploymentSelect, title }) => (
  <Grid container spacing={3}>
    {title && (
      <Grid item xs={12}>
        <Typography variant="h6" style={{ marginBottom: '16px' }}>
          {title}
        </Typography>
      </Grid>
    )}
    {deployments.map((deployment: ProcessedDeployment) => (
      <Grid item xs={12} md={6} lg={4} key={deployment.id}>
        <Card 
          elevation={1} 
          style={{ 
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onClick={() => onDeploymentSelect(deployment)}
        >
          <CardContent>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div style={{ flex: 1 }}>
                <Typography variant="h6" style={{ marginBottom: '4px' }}>
                  {deployment.title}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: '4px' }}>
                  {deployment.environment} • {deployment.deploymentType}
                </Typography>
                <Typography variant="caption" color="textSecondary" style={{ marginBottom: '8px', display: 'block' }}>
                  Release: {deployment.releaseName}
                </Typography>
              </div>
              {getDeploymentStatusIcon(deployment.status)}
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              {getDeploymentStatusChip(deployment.status)}
              {deployment.approvalStatus && deployment.approvalStatus !== 'none' && (
                <Chip 
                  size="small" 
                  label={`Approval: ${deployment.approvalStatus}`}
                  variant="outlined" 
                  style={{ marginLeft: '8px' }}
                />
              )}
            </div>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Assigned To</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {deployment.assignedTo.displayName}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Target Date</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {deployment.targetDate ? formatDateShort(deployment.targetDate) : 'TBD'}
                </Typography>
              </Grid>
            </Grid>

            <div style={{ marginTop: '12px' }}>
              <Typography variant="caption" color="textSecondary">
                Created: {formatDateShort(deployment.createdDate)}
                {deployment.duration && ` • Duration: ${deployment.duration}m`}
              </Typography>
            </div>
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

// Current week deployments component
interface CurrentWeekDeploymentsProps {
  weeklyData: FilteredWeeklyDeployments;
  onDeploymentSelect: (deployment: ProcessedDeployment) => void;
}

export const CurrentWeekDeployments: React.FC<CurrentWeekDeploymentsProps> = ({ weeklyData, onDeploymentSelect }) => (
  <div>
    <Typography variant="h6" style={{ marginBottom: '16px' }}>
      Current Week Beta/Prod Deployments ({weeklyData.weekStartDate} - {weeklyData.weekEndDate})
    </Typography>
    
    <Grid container spacing={3} style={{ marginBottom: '20px' }}>
      <Grid item xs={12} sm={3}>
        <Card elevation={1}>
          <CardContent style={{ textAlign: 'center' }}>
            <Typography variant="h5" style={{ color: '#2196f3' }}>
              {weeklyData.totalDeployments}
            </Typography>
            <Typography variant="body2" color="textSecondary">Total</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={3}>
        <Card elevation={1}>
          <CardContent style={{ textAlign: 'center' }}>
            <Typography variant="h5" style={{ color: '#4caf50' }}>
              {weeklyData.completedDeployments}
            </Typography>
            <Typography variant="body2" color="textSecondary">Completed</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={3}>
        <Card elevation={1}>
          <CardContent style={{ textAlign: 'center' }}>
            <Typography variant="h5" style={{ color: '#f44336' }}>
              {weeklyData.failedDeployments}
            </Typography>
            <Typography variant="body2" color="textSecondary">Failed</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={3}>
        <Card elevation={1}>
          <CardContent style={{ textAlign: 'center' }}>
            <Typography variant="h5" style={{ color: '#ff9800' }}>
              {weeklyData.inProgressDeployments}
            </Typography>
            <Typography variant="body2" color="textSecondary">In Progress</Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>

    {weeklyData.deployments.length > 0 ? (
      <DeploymentList 
        deployments={weeklyData.deployments} 
        onDeploymentSelect={onDeploymentSelect} 
      />
    ) : (
      <Card elevation={1}>
        <CardContent style={{ textAlign: 'center', padding: '40px' }}>
          <CloudDownload style={{ fontSize: 60, color: '#9e9e9e', marginBottom: '16px' }} />
          <Typography variant="h6" style={{ marginBottom: '8px' }}>
            No Beta/Prod Deployments This Week
          </Typography>
          <Typography variant="body2" color="textSecondary">
            No Beta or Production deployments are scheduled for the current week
          </Typography>
        </CardContent>
      </Card>
    )}
  </div>
);

// Past 4 weeks deployments component
interface Past4WeeksDeploymentsProps {
  past4WeeksData: FilteredWeeklyDeployments[];
  onDeploymentSelect: (deployment: ProcessedDeployment) => void;
}

export const Past4WeeksDeployments: React.FC<Past4WeeksDeploymentsProps> = ({ past4WeeksData, onDeploymentSelect }) => (
  <div>
    <Typography variant="h6" style={{ marginBottom: '16px' }}>
      Past 4 Weeks Production Deployments (Completed Only)
    </Typography>
    
    {past4WeeksData.map((week: FilteredWeeklyDeployments, weekIndex: number) => (
      <Card key={weekIndex} style={{ marginBottom: '20px' }} elevation={1}>
        <CardContent>
          <Typography variant="h6" style={{ marginBottom: '12px' }}>
            Week {weekIndex + 1}: {week.weekStartDate} to {week.weekEndDate}
          </Typography>
          
          <Grid container spacing={2} style={{ marginBottom: '16px' }}>
            <Grid item xs={3}>
              <Typography variant="body2" color="textSecondary">Total Prod</Typography>
              <Typography variant="h6" style={{ color: '#2196f3' }}>
                {week.totalDeployments}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="body2" color="textSecondary">Completed</Typography>
              <Typography variant="h6" style={{ color: '#4caf50' }}>
                {week.completedDeployments}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="body2" color="textSecondary">Failed</Typography>
              <Typography variant="h6" style={{ color: '#f44336' }}>
                {week.failedDeployments}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="body2" color="textSecondary">Success Rate</Typography>
              <Typography variant="h6" style={{ color: '#4caf50' }}>
                {week.totalDeployments > 0 ? ((week.completedDeployments / week.totalDeployments) * 100).toFixed(1) : 0}%
              </Typography>
            </Grid>
          </Grid>

          {week.deployments.length > 0 ? (
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {week.deployments.slice(0, 10).map((deployment: ProcessedDeployment, index: number) => (
                <div key={deployment.id} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: index < Math.min(week.deployments.length - 1, 9) ? '1px solid #f0f0f0' : 'none',
                  cursor: 'pointer'
                }}
                onClick={() => onDeploymentSelect(deployment)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {getDeploymentStatusIcon(deployment.status)}
                    <div>
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {deployment.title}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {deployment.environment} • {deployment.assignedTo.displayName}
                      </Typography>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {getDeploymentStatusChip(deployment.status)}
                    <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: '4px' }}>
                      {formatDateShort(deployment.completedDate || deployment.createdDate)}
                    </Typography>
                  </div>
                </div>
              ))}
              {week.deployments.length > 10 && (
                <Typography variant="caption" color="textSecondary" style={{ padding: '8px 0', display: 'block', textAlign: 'center' }}>
                  ... and {week.deployments.length - 10} more production deployments
                </Typography>
              )}
            </div>
          ) : (
            <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center', padding: '20px' }}>
              No completed production deployments for this week
            </Typography>
          )}
        </CardContent>
      </Card>
    ))}

    {past4WeeksData.length === 0 && (
      <Card elevation={1}>
        <CardContent style={{ textAlign: 'center', padding: '40px' }}>
          <Timeline style={{ fontSize: 60, color: '#9e9e9e', marginBottom: '16px' }} />
          <Typography variant="h6" style={{ marginBottom: '8px' }}>
            No Production Deployment History
          </Typography>
          <Typography variant="body2" color="textSecondary">
            No completed production deployments available for the past 4 weeks
          </Typography>
        </CardContent>
      </Card>
    )}
  </div>
);

// Production environment stats component
interface ProductionStatsProps {
  environments: FilteredProjectDeploymentData['summary']['environments'];
}

export const ProductionStats: React.FC<ProductionStatsProps> = ({ environments }) => (
  <div>
    <Typography variant="h6" style={{ marginBottom: '16px' }}>
      Production Environment Statistics
    </Typography>
    
    <Grid container spacing={3}>
      {environments?.map((env: NonNullable<ProductionStatsProps['environments']>[number], index: number) => (
        <Grid item xs={12} md={6} lg={4} key={index}>
          <Card elevation={1}>
            <CardContent>
              <Typography variant="h6" style={{ marginBottom: '12px' }}>
                {env.name}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Total Deployments</Typography>
                  <Typography variant="h6" style={{ color: '#2196f3' }}>
                    {env.deploymentCount}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Success Rate</Typography>
                  <Typography variant="h6" style={{ color: '#4caf50' }}>
                    {env.successRate.toFixed(1)}%
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>

    {environments?.length === 0 && (
      <Card elevation={1}>
        <CardContent style={{ textAlign: 'center', padding: '40px' }}>
          <Build style={{ fontSize: 60, color: '#9e9e9e', marginBottom: '16px' }} />
          <Typography variant="h6" style={{ marginBottom: '8px' }}>
            No Production Environment Data
          </Typography>
          <Typography variant="body2" color="textSecondary">
            No production environment statistics available
          </Typography>
        </CardContent>
      </Card>
    )}
  </div>
);

// Recent deployments component
interface RecentDeploymentsProps {
  recentData: FilteredProjectDeploymentData['recentDeployments'];
  onDeploymentSelect: (deployment: ProcessedDeployment) => void;
}

export const RecentDeployments: React.FC<RecentDeploymentsProps> = ({ recentData, onDeploymentSelect }) => (
  <div>
    <Typography variant="h6" style={{ marginBottom: '16px' }}>
      Recent Completed Production Deployments
    </Typography>
    
    <Grid container spacing={3} style={{ marginBottom: '20px' }}>
      <Grid item xs={12} sm={4}>
        <Card elevation={1}>
          <CardContent style={{ textAlign: 'center' }}>
            <Typography variant="h5" style={{ color: '#2196f3' }}>
              {recentData.totalDeployments}
            </Typography>
            <Typography variant="body2" color="textSecondary">Recent</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Card elevation={1}>
          <CardContent style={{ textAlign: 'center' }}>
            <Typography variant="h5" style={{ color: '#4caf50' }}>
              {recentData.completedDeployments}
            </Typography>
            <Typography variant="body2" color="textSecondary">Completed</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Card elevation={1}>
          <CardContent style={{ textAlign: 'center' }}>
            <Typography variant="h5" style={{ color: '#f44336' }}>
              {recentData.failedDeployments}
            </Typography>
            <Typography variant="body2" color="textSecondary">Failed</Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>

    {recentData.deployments.length > 0 ? (
      <Card elevation={1}>
        <CardContent>
          <Typography variant="h6" style={{ marginBottom: '16px' }}>
            Recent Production Deployment Details
          </Typography>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {recentData.deployments.map((deployment: ProcessedDeployment, index: number) => (
              <div key={deployment.id} style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '12px 0',
                borderBottom: index < recentData.deployments.length - 1 ? '1px solid #f0f0f0' : 'none',
                cursor: 'pointer'
              }}
              onClick={() => onDeploymentSelect(deployment)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {getDeploymentStatusIcon(deployment.status)}
                  <div>
                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                      {deployment.title}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {deployment.environment} • {deployment.deploymentType} • {deployment.assignedTo.displayName}
                    </Typography>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {getDeploymentStatusChip(deployment.status)}
                  <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: '4px' }}>
                    {deployment.duration ? `${deployment.duration}m` : 'No duration'} • {formatDateShort(deployment.completedDate || deployment.createdDate)}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    ) : (
      <Card elevation={1}>
        <CardContent style={{ textAlign: 'center', padding: '40px' }}>
          <Schedule style={{ fontSize: 60, color: '#9e9e9e', marginBottom: '16px' }} />
          <Typography variant="h6" style={{ marginBottom: '8px' }}>
            No Recent Production Deployments
          </Typography>
          <Typography variant="body2" color="textSecondary">
            No recent completed production deployments were found
          </Typography>
        </CardContent>
      </Card>
    )}
  </div>
);

// Deployment tabs component
interface DeploymentTabsProps {
  deploymentData: FilteredProjectDeploymentData;
  tabValue: number;
  onTabChange: (event: React.ChangeEvent<{}>, newValue: number) => void;
  onDeploymentSelect: (deployment: ProcessedDeployment) => void;
}

export const DeploymentTabs: React.FC<DeploymentTabsProps> = ({ 
  deploymentData, 
  tabValue, 
  onTabChange, 
  onDeploymentSelect 
}) => (
  <Card>
    <Tabs
      value={tabValue}
      onChange={onTabChange}
      indicatorColor="primary"
      textColor="primary"
      variant="scrollable"
      scrollButtons="auto"
    >
      <Tab label="Current Week Beta/Prod" />
      <Tab label="Past 4 Weeks Prod" />
      <Tab label="Production Stats" />
      <Tab label="Recent Prod Deployments" />
    </Tabs>

    <TabPanel value={tabValue} index={0}>
      <CurrentWeekDeployments 
        weeklyData={deploymentData.currentWeekDeployments} 
        onDeploymentSelect={onDeploymentSelect} 
      />
    </TabPanel>

    <TabPanel value={tabValue} index={1}>
      <Past4WeeksDeployments 
        past4WeeksData={deploymentData.past4WeeksDeployments} 
        onDeploymentSelect={onDeploymentSelect} 
      />
    </TabPanel>

    <TabPanel value={tabValue} index={2}>
      <ProductionStats environments={deploymentData.summary?.environments} />
    </TabPanel>

    <TabPanel value={tabValue} index={3}>
      <RecentDeployments 
        recentData={deploymentData.recentDeployments} 
        onDeploymentSelect={onDeploymentSelect} 
      />
    </TabPanel>
  </Card>
);

// Deployment detail view component
interface DeploymentDetailProps {
  deployment: ProcessedDeployment;
}

export const DeploymentDetail: React.FC<DeploymentDetailProps> = ({ deployment }) => (
  <div>
    <Card style={{ marginBottom: '20px' }}>
      <CardContent>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div>
            <Typography variant="h4" component="h2" style={{ marginBottom: '8px' }}>
              {deployment.title}
            </Typography>
            <Typography variant="body1" color="textSecondary" style={{ marginBottom: '8px' }}>
              {deployment.description}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px' }}>
              <strong>Environment:</strong> {deployment.environment} • 
              <strong>Type:</strong> {deployment.deploymentType} • 
              <strong>Release:</strong> {deployment.releaseName}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <Link 
                to={deployment.workItemUrl} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                View in Azure DevOps
              </Link>
            </Typography>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {getDeploymentStatusIcon(deployment.status)}
            {getDeploymentStatusChip(deployment.status)}
            {deployment.approvalStatus && deployment.approvalStatus !== 'none' && (
              <Chip 
                size="small" 
                label={`Approval: ${deployment.approvalStatus}`}
                variant="outlined"
              />
            )}
          </div>
        </div>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <div>
              <Typography variant="h6">{deployment.assignedTo.displayName}</Typography>
              <Typography variant="body2" color="textSecondary">Assigned To</Typography>
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <div>
              <Typography variant="h6">
                {deployment.targetDate ? formatDate(deployment.targetDate) : 'TBD'}
              </Typography>
              <Typography variant="body2" color="textSecondary">Target Date</Typography>
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <div>
              <Typography variant="h6">
                {deployment.completedDate ? formatDate(deployment.completedDate) : 'Not Completed'}
              </Typography>
              <Typography variant="body2" color="textSecondary">Completed Date</Typography>
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <div>
              <Typography variant="h6">
                {deployment.duration ? `${deployment.duration} min` : 'N/A'}
              </Typography>
              <Typography variant="body2" color="textSecondary">Duration</Typography>
            </div>
          </Grid>
        </Grid>
      </CardContent>
    </Card>

    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Typography variant="h6" component="h3" style={{ marginBottom: '16px' }}>
              Deployment Timeline & Phases
            </Typography>
            
            {deployment.deployPhases && deployment.deployPhases.length > 0 ? (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {deployment.deployPhases.map((phase: any, index: number) => (
                  <Card key={index} variant="outlined" style={{ marginBottom: '12px' }}>
                    <CardContent style={{ padding: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <Typography variant="h6">
                          {phase.name}
                        </Typography>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {getDeploymentStatusIcon(phase.status)}
                          <Chip 
                            size="small" 
                            label={phase.status} 
                            style={{ 
                              backgroundColor: getDeploymentStatusColor(phase.status), 
                              color: 'white' 
                            }}
                          />
                        </div>
                      </div>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">Started:</Typography>
                          <Typography variant="body2">
                            {phase.startedOn ? formatDate(phase.startedOn) : 'Not started'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">Completed:</Typography>
                          <Typography variant="body2">
                            {phase.completedOn ? formatDate(phase.completedOn) : 'Not completed'}
                          </Typography>
                        </Grid>
                      </Grid>

                      {phase.startedOn && phase.completedOn && (
                        <div style={{ marginTop: '12px' }}>
                          <Typography variant="body2" color="textSecondary">Duration:</Typography>
                          <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                            {Math.round((new Date(phase.completedOn).getTime() - new Date(phase.startedOn).getTime()) / (1000 * 60))} minutes
                          </Typography>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Typography variant="body2" color="textSecondary" style={{ padding: '20px', textAlign: 'center' }}>
                No deployment phases available for this deployment
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={4}>
        <Card style={{ marginBottom: '20px' }}>
          <CardContent>
            <Typography variant="h6" component="h3" style={{ marginBottom: '16px' }}>
              Deployment Information
            </Typography>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <Typography variant="body2" color="textSecondary">Release Definition:</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {deployment.releaseDefinitionName}
                </Typography>
              </div>
              
              <div>
                <Typography variant="body2" color="textSecondary">Release:</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {deployment.releaseName}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  ID: {deployment.releaseId}
                </Typography>
              </div>
              
              <div>
                <Typography variant="body2" color="textSecondary">Environment Rank:</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {deployment.environmentRank}
                </Typography>
              </div>
              
              <div>
                <Typography variant="body2" color="textSecondary">Trigger Reason:</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {deployment.triggerReason}
                </Typography>
              </div>
              
              <div>
                <Typography variant="body2" color="textSecondary">Created Date:</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {formatDate(deployment.createdDate)}
                </Typography>
              </div>
              
              <div>
                <Typography variant="body2" color="textSecondary">Last Updated:</Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {formatDate(deployment.lastUpdated)}
                </Typography>
              </div>
              
              {deployment.relatedWorkItems > 0 && (
                <div>
                  <Typography variant="body2" color="textSecondary">Related Work Items:</Typography>
                  <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                    {deployment.relatedWorkItems}
                  </Typography>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {deployment.tags.length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" component="h3" style={{ marginBottom: '16px' }}>
                Tags
              </Typography>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {deployment.tags.map((tag: string, index: number) => (
                  <Chip 
                    key={index}
                    label={tag}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </Grid>
    </Grid>
  </div>
);